import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthPage from './components/Auth';
import PlantTracker from './components/PlantTracker';
import LandingPage from './components/LandingPage';
import { LogOut, User, AlertCircle } from 'lucide-react';

const AppContent = () => {
  const { user, logout, loading, isAuthenticated } = useAuth();
  const [showLanding, setShowLanding] = useState(true);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showAuthPage, setShowAuthPage] = useState(false);
  const [prefilledEmail, setPrefilledEmail] = useState('');
  const [guestPlants, setGuestPlants] = useState([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  useEffect(() => {
    // Check if user has seen the app before
    const hasVisited = localStorage.getItem('hasVisited');
    const guestData = localStorage.getItem('guestPlants');
    
    if (guestData) {
      setGuestPlants(JSON.parse(guestData));
    }
    
    // If authenticated, hide landing page
    if (isAuthenticated) {
      setShowLanding(false);
      setIsGuestMode(false);
    } else if (hasVisited && !isAuthenticated) {
      // Return visitor, show landing page
      setShowLanding(true);
    }
  }, [isAuthenticated]);

  const handleGetStarted = (email = '') => {
    setPrefilledEmail(email);
    setShowAuthPage(true);
    setShowLanding(false);
    
    // Track signup intent
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'signup_started', {
        event_category: 'engagement',
        method: email ? 'quick_signup' : 'cta_button'
      });
    }
  };

  const handleLogin = () => {
    setShowAuthPage(true);
    setShowLanding(false);
  };

  const handleTryDemo = () => {
    setIsGuestMode(true);
    setShowLanding(false);
    localStorage.setItem('hasVisited', 'true');
    
    // Track demo start
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'demo_started', {
        event_category: 'engagement'
      });
    }
  };

  const handleGuestDataUpdate = (plants) => {
    setGuestPlants(plants);
    localStorage.setItem('guestPlants', JSON.stringify(plants));
    
    // Show upgrade prompt after adding first plant in guest mode
    if (plants.length === 1 && !showUpgradePrompt) {
      setShowUpgradePrompt(true);
    }
  };

  const handleUpgradeFromGuest = () => {
    setShowAuthPage(true);
    setShowUpgradePrompt(false);
    
    // Track conversion from guest
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'guest_to_signup', {
        event_category: 'conversion',
        value: guestPlants.length
      });
    }
  };

  const handleBackToLanding = () => {
    setShowAuthPage(false);
    setShowLanding(true);
    setIsGuestMode(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show landing page for new visitors
  if (showLanding && !isAuthenticated) {
    return (
      <LandingPage 
        onGetStarted={handleGetStarted}
        onLogin={handleLogin}
        onTryDemo={handleTryDemo}
      />
    );
  }

  // Show auth page
  if (showAuthPage && !isAuthenticated) {
    return (
      <AuthPage 
        prefilledEmail={prefilledEmail}
        guestPlants={guestPlants}
        onBack={handleBackToLanding}
      />
    );
  }

  // Guest Mode
  if (isGuestMode && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        {/* Guest Mode Header */}
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center">
                <AlertCircle className="text-amber-600 mr-2" size={20} />
                <span className="text-amber-800 font-medium">
                  Guest Mode - Your data is only saved locally
                </span>
              </div>
              <button
                onClick={handleUpgradeFromGuest}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition"
              >
                Create Free Account to Save
              </button>
            </div>
          </div>
        </div>
        
        <PlantTracker 
          isGuestMode={true}
          onDataUpdate={handleGuestDataUpdate}
          initialPlants={guestPlants}
        />

        {/* Upgrade Prompt Modal */}
        {showUpgradePrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">
              <h2 className="text-2xl font-bold mb-3">Great Start! 🌱</h2>
              <p className="text-gray-600 mb-6">
                You've added your first plant! Create a free account to:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Save your plants permanently</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Access from any device</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Get watering reminders</span>
                </li>
              </ul>
              <button
                onClick={handleUpgradeFromGuest}
                className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold mb-3 transition"
              >
                Create Free Account
              </button>
              <button
                onClick={() => setShowUpgradePrompt(false)}
                className="w-full text-gray-500 hover:text-gray-700"
              >
                Continue as Guest
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Authenticated User View
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* User header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <User className="text-green-600 mr-2" size={20} />
            <span className="text-gray-700">
              Welcome back, {user.firstName}!
            </span>
          </div>
          <button
            onClick={logout}
            className="flex items-center text-gray-600 hover:text-red-600 transition-colors"
          >
            <LogOut size={16} className="mr-1" />
            Sign Out
          </button>
        </div>
      </div>
      
      <PlantTracker />
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;